/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any*/
import { FAQ, FaqResponse, Podcast, PodcastResponse, Post, PostResponse } from 'src/components/models';
import ApiConfig from './ApiConfig';
import { ConsumerInformationClient_V1, InformationalVideoDTO, ChecklistDTO, WebsiteContentLinkDTO, WebsitePostCategoryDTO } from './generated/api.generated.v1';
import axios from 'axios';
import moment from 'moment';
import { AppStore } from 'src/store';

interface httpPlugin {
    get: (url: string, params: any, headers: any, succes: any, failure: any) => void
};

const getHttpPlugin = () => {
    const anyCordova = cordova as any;
    if (anyCordova && anyCordova.plugin) {
        return anyCordova.plugin.http as httpPlugin;
    }

    return null;
}

export default class ConsumerInformationService {
    private readonly _consumerInfoClient: ConsumerInformationClient_V1;
    private readonly _postsPerPage = 100;

    constructor() {
        this._consumerInfoClient = new ConsumerInformationClient_V1(new ApiConfig(), process.env.VUE_APP_API_V1_URL);
    }

    private static instance: ConsumerInformationService;

    public static GetInstance(): ConsumerInformationService {
        if (!ConsumerInformationService.instance) {
            ConsumerInformationService.instance = new ConsumerInformationService();
        }

        return ConsumerInformationService.instance;
    }

    public GetInformationalVideos(): Promise<Array<InformationalVideoDTO>> {
        return this._consumerInfoClient.videos();
    }

    public Checklists(): Promise<Array<ChecklistDTO>> {
        return this._consumerInfoClient.checklists();
    }

    public WebsiteContentLinks(): Promise<WebsiteContentLinkDTO> {
        return this._consumerInfoClient.websiteContentLinks();
    }

    public WebsitePostCategories(): Promise<Array<WebsitePostCategoryDTO>> {
        return this._consumerInfoClient.websitePostCategories();
    }

    public async BlogPosts(postsUrl: string, pageNo = 1): Promise<Array<Post> | null> {
        const results = await this.GetPosts(postsUrl, pageNo);
        if (results && results.length == this._postsPerPage) {
            const posts = await this.BlogPosts(postsUrl, pageNo + 1);
            const addedResult = posts ? results.concat(posts) : results;
            return addedResult;
        } else {
            return results;
        }
    }

    private async GetPosts(postsUrl: string, page: number): Promise<Array<Post> | null> {
        const mapper = (data: Array<PostResponse>) => {
            const results = data.map(x => {
                const post = new Post();
                post.id = x.id;
                post.slug = x.slug;
                post.categories = x.categories;
                post.date = moment(x.yoast_head_json.article_published_time);
                post.title = x.yoast_head_json.og_title;
                post.bannerImageUrl = x.acf.banner_image.url;
                post.content = x.acf.styled_blocks[0].content;
                post.relatedStoryIds = [];
                post.relatedStoryIds.push(x.acf.related_stories.left.ID, x.acf.related_stories.center.ID, x.acf.related_stories.right.ID);
                return post;
            });

            return results;
        }

        const queryUrl = postsUrl + `?per_page=${this._postsPerPage}&page=${page}`;

        return await this.GetData<Array<Post> | null>(queryUrl, mapper);
    }

    public async FrequentlyAskedQuestions(postsUrl: string): Promise<FAQ | null> {
        if (!postsUrl) {
            return null;
        }

        const mapper = (data: FaqResponse) => {
            const faq = new FAQ();
            faq.title = data.yoast_head_json.og_title;

            const categories = data.acf.faq_category.map(x => {
                return {
                    title: x.category_title,
                    questions: x.faq.map(x => { return { question: x.question, answerContent: x.answer } }
                    )
                };
            })
            faq.categories = categories
            faq.glossary = data.acf.glossary_entry.map(x => { return { term: x.term, definition: x.definition } });

            return faq;
        }

        return await this.GetData<FAQ | null>(postsUrl, mapper);

    }

    public async Podcasts(postsUrl: string, pageNo = 1): Promise<Array<Podcast> | null> {
        const results = await this.GetPodcasts(postsUrl, pageNo);
        if (results && results.length == this._postsPerPage) {
            const posts = await this.GetPodcasts(postsUrl, pageNo + 1);
            const addedResult = posts ? results.concat(posts) : results;
            return addedResult;
        } else {
            return results;
        }
    }

    private async GetPodcasts(postsUrl: string, page: number): Promise<Array<Podcast> | null> {
        if (!postsUrl) {
            return null;
        }

        const mapper = (data: Array<PodcastResponse>) => {
            return data.map(x => {
                const podcast = new Podcast();
                podcast.title = x.yoast_head_json.og_title;
                podcast.imageUrl = x.yoast_head_json.og_image[0].url;
                podcast.linkUrl = x.acf.episode_url;
                podcast.description = x.yoast_head_json.og_description;
                return podcast;
            });

        }

        const queryUrl = postsUrl + `?per_page=${this._postsPerPage}&page=${page}`;
        
        return await this.GetData<Array<Podcast> | null>(queryUrl, mapper)

    }

    private async GetData<TResult>(url: string, mapper: (res: any) => TResult): Promise<TResult | null> {
        return new Promise<TResult | null>((resolve, reject) => {
            if (!url) {
                resolve(null);
            }

            const makeNativeApiCall = () => {
                const successCallback = (resp: any) => {
                    const data = resp && resp.data
                        ? JSON.parse(resp.data)
                        : [];

                    const results = mapper(data);

                    resolve(results);
                }
                const httpPlugin = getHttpPlugin();
                if (httpPlugin) {
                    httpPlugin.get(url, {}, {}, successCallback, (res: any) => { reject(res) });
                }
            }

            const makeWebApiCall = () => {
                axios.get<TResult>(url)
                    .then(resp => {
                        const results = mapper(resp.data);
                        resolve(results);
                    })
                    .catch((er) => {
                        reject(er);
                    });
            }

            if (AppStore.state.setting.platform === 'ios') {
                makeNativeApiCall();
            } else {
                makeWebApiCall();
            }
        })
    }
}